import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { getShopRanges, searchRanges } from "../config/ranges";
import { PRODUCT_CATEGORIES } from "../config/categories";
import RangeCard from "../components/product/RangeCard";
import Footer from "../components/Footer";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

/**
 * Shop uses the SAME Product Master / Range data as Products.
 * Initially quote-only — no fake pricing or stock.
 */
export default function ShopPage() {
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  useDocumentMeta({
    title: "Shop | DEBSUN Technologies",
    description:
      "DEBSUN Shop — browse engineering product ranges in Quote Mode. Same Product Master as the catalogue.",
  });

  const ranges = useMemo(() => {
    let list = query ? searchRanges(query) : getShopRanges();
    const shopIds = new Set(getShopRanges().map((r) => r.id));
    list = list.filter((r) => shopIds.has(r.id));
    if (category !== "all") {
      list = list.filter((r) => r.category === category);
    }
    return list;
  }, [category, query]);

  return (
    <div className="min-h-screen pt-28" data-testid="shop-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24">
        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mb-3">
          § Commerce Foundation · Quote Mode
        </div>
        <h1 className="display display-hero text-4xl md:text-6xl tracking-[-0.03em]">
          Shop
        </h1>
        <p className="mt-4 text-base text-steel-text leading-relaxed max-w-2xl">
          The Shop and Products catalogue share one Product Master. Browse product
          families, open a variant and request a quote. Pricing is not published
          at this stage.
        </p>

        <div className="mt-8 border border-border bg-secondary/40 p-5 text-sm text-steel-text leading-relaxed max-w-3xl">
          Fixed-price checkout remains available for future commerce enablement.
          Engineering products below are quoted through DEBSUN Technologies.
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          <button
            onClick={() => setCategory("all")}
            className={`font-mono-2 text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 border transition-colors ${
              category === "all"
                ? "border-[color:var(--gold)] text-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            All
          </button>
          {PRODUCT_CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`font-mono-2 text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 border transition-colors ${
                category === c.id
                  ? "border-[color:var(--gold)] text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-6">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search product ranges…"
            className="w-full max-w-md bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--gold)]"
            data-testid="shop-search-input"
          />
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ranges.map((r, i) => (
            <RangeCard key={r.id} range={r} index={i} mode="shop" />
          ))}
        </div>

        {ranges.length === 0 && (
          <div className="mt-16 text-center text-muted-foreground">
            No product ranges match the current filters.
          </div>
        )}

        <div className="mt-16 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between border border-border p-6">
          <p className="text-sm text-steel-text leading-relaxed max-w-xl">
            Prefer the engineering catalogue view? Products and Shop use the same
            underlying data.
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 font-mono-2 text-[11px] tracking-[0.2em] uppercase"
            style={{ color: "var(--gold)" }}
          >
            Open Products <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
